/* Hushlea theme script (vanilla, deferred). Ports the Whop storefront's header, announcement countdown and cart
   (whop-app src/components/layout/*, src/components/cart*, src/lib/cart.ts) onto Shopify's AJAX cart.

   Cart API for sections and the browser console:
     HushCart.add([{ id: variantId, quantity: 1, properties: {...} }], { openDrawer: true })  -> Promise<cart/add.js json>
     HushCart.change(line, quantity)  -> Promise<cart json> (line is 1-based, as /cart/change.js)
     HushCart.open() / HushCart.close() / HushCart.refresh()
   Free extras are real $0 lines with the line property _gift_of = their kit's variant id. After every change they
   are kept at quantity 1 while that kit is in the cart, and removed with it (SleepEase's drawer rule).
   Every change re-renders the drawer, the header cart icon and any element marked data-hush-cart-section, then
   fires a "hush:cart" event on document with the cart (or add.js response) as detail.

   On layout/dawn.liquid this loads after Dawn's global.js: custom elements Dawn already defined are left alone. */
(() => {
  const define = (name, cls) => customElements.get(name) || customElements.define(name, cls);

  /* Announcement bar: countdown to midnight in the bar's time zone (AnnouncementBar.tsx). */
  function timeLeft(timeZone) {
    const now = new Date();
    const local = new Date(now.toLocaleString('en-US', { timeZone }));
    const midnight = new Date(local);
    midnight.setHours(24, 0, 0, 0);
    const diff = midnight - local;
    const pad = (n) => String(n).padStart(2, '0');
    return [Math.floor((diff % 86400000) / 3600000), Math.floor((diff % 3600000) / 60000), Math.floor((diff % 60000) / 1000)].map(pad);
  }
  function tickCountdowns() {
    document.querySelectorAll('[data-countdown-timezone]').forEach((el) => {
      let parts;
      try {
        parts = timeLeft(el.dataset.countdownTimezone || 'America/New_York');
      } catch {
        parts = timeLeft('America/New_York');
      }
      el.querySelectorAll('.time').forEach((span, i) => (span.textContent = parts[i]));
    });
  }
  tickCountdowns();
  setInterval(tickCountdowns, 1000);

  /* Dawn's <sticky-header data-sticky-type="on-scroll-up">: hides on scroll down, slides back on scroll up. */
  define(
    'sticky-header',
    class extends HTMLElement {
      connectedCallback() {
        const section = this.closest('.section-header');
        if (!section) return;
        const setHeight = () => document.documentElement.style.setProperty('--header-height', `${section.offsetHeight}px`);
        setHeight();
        window.matchMedia('(max-width: 990px)').addEventListener('change', setHeight);
        const bottom = section.getBoundingClientRect().bottom + window.scrollY;
        let last = 0;
        window.addEventListener(
          'scroll',
          () => {
            const top = window.scrollY;
            if (top > last && top > bottom) {
              section.classList.add('scrolled-past-header');
              requestAnimationFrame(() => section.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky'));
            } else if (top < last && top > bottom) {
              section.classList.add('scrolled-past-header');
              requestAnimationFrame(() => {
                section.classList.add('shopify-section-header-sticky', 'animate');
                section.classList.remove('shopify-section-header-hidden');
              });
            } else if (top <= 0) {
              section.classList.remove('scrolled-past-header');
              requestAnimationFrame(() => section.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate'));
            }
            last = top;
          },
          { passive: true }
        );
      }
    }
  );

  /* Dawn's HeaderDrawer: details[open] + .menu-opening, body scroll lock, 400ms close. */
  define(
    'header-drawer',
    class extends HTMLElement {
      connectedCallback() {
        const details = this.querySelector('details');
        const summary = details.querySelector('summary');
        const section = this.closest('.section-header');
        const root = document.documentElement;
        const setBottom = () => {
          if (section) root.style.setProperty('--header-bottom-position', `${Math.floor(section.getBoundingClientRect().bottom - 1)}px`);
          root.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        };
        const open = () => {
          setBottom();
          section?.classList.add('menu-open');
          setTimeout(() => details.classList.add('menu-opening'));
          summary.setAttribute('aria-expanded', 'true');
          window.addEventListener('resize', setBottom);
          document.body.classList.add('overflow-hidden-tablet');
        };
        const close = () => {
          details.classList.remove('menu-opening');
          summary.setAttribute('aria-expanded', 'false');
          document.body.classList.remove('overflow-hidden-tablet');
          section?.classList.remove('menu-open');
          window.removeEventListener('resize', setBottom);
          setTimeout(() => details.removeAttribute('open'), 400);
        };
        summary.addEventListener('click', (event) => {
          if (details.hasAttribute('open')) {
            event.preventDefault();
            close();
          } else open();
        });
        details.addEventListener('keyup', (event) => event.key === 'Escape' && details.hasAttribute('open') && close());
      }
    }
  );

  /* Dawn's QuantityInput: the +/- buttons step the input and fire "change" (the cart listens for that). */
  define(
    'quantity-input',
    class extends HTMLElement {
      connectedCallback() {
        const input = this.querySelector('input');
        this.querySelectorAll('button').forEach((button) =>
          button.addEventListener('click', (event) => {
            event.preventDefault();
            if (input.readOnly) return;
            const before = input.value;
            button.name === 'plus' ? input.stepUp() : input.stepDown();
            if (before !== input.value) input.dispatchEvent(new Event('change', { bubbles: true }));
          })
        );
      }
    }
  );

  /* Cart drawer. Also answers Dawn's product-form.js (getSectionsToRender / renderContents / setActiveElement),
     so a stock Dawn product page opens this drawer too. */
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  define(
    'cart-drawer',
    class extends HTMLElement {
      open() {
        this.classList.add('animate');
        setTimeout(() => this.classList.add('active'));
        document.body.classList.add('overflow-hidden');
        this.querySelector('.drawer__inner')?.focus({ preventScroll: true });
      }
      close() {
        this.classList.remove('active');
        document.body.classList.remove('overflow-hidden');
        this.activeElement?.focus?.({ preventScroll: true });
      }
      setActiveElement(element) {
        this.activeElement = element;
      }
      getSectionsToRender() {
        return SECTIONS.map((id) => ({ id }));
      }
      renderContents(response) {
        render(response.sections);
        this.open();
      }
    }
  );

  const drawer = () => document.querySelector('cart-drawer');
  const pageSections = () => [...document.querySelectorAll('[data-hush-cart-section]')].map((el) => el.dataset.hushCartSection);
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  function render(sections) {
    if (!sections) return;
    const el = drawer();
    if (sections['cart-drawer'] && el) {
      const next = parse(sections['cart-drawer']).querySelector('cart-drawer');
      const active = el.classList.contains('active');
      el.className = next.className + (active ? ' animate active' : '');
      el.innerHTML = next.innerHTML;
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (sections['cart-icon-bubble'] && bubble) {
      bubble.innerHTML = parse(sections['cart-icon-bubble']).querySelector('.shopify-section').innerHTML;
    }
    for (const id of pageSections()) {
      if (!sections[id]) continue;
      const current = document.querySelector(`[data-hush-cart-section="${id}"]`);
      const next = parse(sections[id]).querySelector(`[data-hush-cart-section="${id}"]`);
      if (current && next) current.replaceWith(next);
    }
  }

  function showError(message) {
    const box = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (box) box.textContent = message;
  }

  async function post(url, body) {
    const sections = [...SECTIONS, ...pageSections()];
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections, sections_url: window.location.pathname }),
    });
    const json = await response.json();
    if (!response.ok || json.status) {
      const message = json.description || json.message || 'Something went wrong. Please try again.';
      showError(message);
      throw new Error(message);
    }
    render(json.sections);
    document.dispatchEvent(new CustomEvent('hush:cart', { detail: json }));
    if (json.token && json.items) await syncGifts(json);
    return json;
  }

  // Free extras: one each while their kit is in the cart, none without it. Needs the whole cart (change/update.js).
  async function syncGifts(cart) {
    const kits = new Set(cart.items.filter((i) => !(i.properties && i.properties._gift_of)).map((i) => String(i.variant_id)));
    const updates = {};
    for (const item of cart.items) {
      const kit = item.properties && item.properties._gift_of;
      if (!kit) continue;
      const want = kits.has(kit) ? 1 : 0;
      if (item.quantity !== want) updates[item.key] = want;
    }
    if (Object.keys(updates).length) await post(`${root}cart/update.js`, { updates });
  }

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  window.HushCart = {
    add(items, { openDrawer = true } = {}) {
      return post(`${root}cart/add.js`, { items }).then(async (json) => {
        // add.js answers with the added lines only; a second kit bumps its extras to 2, so check the whole cart.
        if (items.some((i) => i.properties && i.properties._gift_of)) await syncGifts(await (await fetch(`${root}cart.js`)).json());
        if (openDrawer) this.open();
        return json;
      });
    },
    change(line, quantity) {
      return post(`${root}cart/change.js`, { line, quantity });
    },
    async refresh() {
      const sections = [...SECTIONS, ...pageSections()];
      const response = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`);
      render(await response.json());
    },
    open() {
      drawer()?.open();
    },
    close() {
      drawer()?.close();
    },
  };

  /* Event delegation: survives every re-render. */
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (target.closest('#cart-icon-bubble') && drawer()) {
      event.preventDefault();
      HushCart.open();
    } else if (target.closest('cart-drawer .drawer__close, #CartDrawer-Overlay')) {
      HushCart.close();
    } else if (target.closest('[data-remove-line]')) {
      event.preventDefault();
      HushCart.change(Number(target.closest('[data-remove-line]').dataset.removeLine), 0).catch(() => {});
    } else if (target.closest('[data-noop]')) {
      event.preventDefault();
    }
  });
  document.addEventListener('keyup', (event) => event.key === 'Escape' && drawer()?.classList.contains('active') && HushCart.close());
  document.addEventListener('change', (event) => {
    const input = event.target.closest('input[data-line]');
    if (!input) return;
    const quantity = parseInt(input.value, 10);
    const min = parseInt(input.dataset.min || input.min || '0', 10);
    // Dawn's data-min: the cart page never goes below 1 by typing or the minus button; the bin removes.
    if (Number.isNaN(quantity) || quantity < min) {
      input.value = input.defaultValue;
      return;
    }
    HushCart.change(Number(input.dataset.line), quantity).catch(() => (input.value = input.defaultValue));
  });

  // Back/forward cache can restore a stale cart: re-read it.
  window.addEventListener('pageshow', (event) => event.persisted && HushCart.refresh());
})();
